import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DrawIcon from "@mui/icons-material/Draw";
import EmailIcon from "@mui/icons-material/Email";
import PrintIcon from "@mui/icons-material/Print";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import type { Editor } from "tinymce";

import { neutral } from "app/themes/colors";
import CircularProgress from "components/circular-progress";
import FillInTheBlanksPanel from "components/custom-print-form-preview/fill-in-the-blanks-panel.component";
import useCustomPrintFormPreview from "components/custom-print-form-preview/use-custom-print-form-preview.hook";
import Dialog from "components/dialog";
import { SignDocumentIcon } from "components/icons";
import SaveToEdocsDialog from "components/save-to-edocs-dialog";
import SignaturePicker from "components/signature-picker";
import WordProcessorField from "components/word-processor-field";
import { NON_BREAKABLE_SPACE } from "constants/common";
import {
  HTML_FORMS_FEATURE,
  PURCHASE_AGREEMENT_FOOTER_SIGNER,
  PURCHASE_AGREEMENT_TEMPLATES_SECTION,
  VERTICAL_SOUTHWESTERN_LIMITATIONS
} from "constants/feature-flags";
import { PROMPT_TYPE_TEXT_TEMPLATE } from "constants/prompts";
import {
  PRINTABLE_FORM_TEMPLATE_TYPES,
  TEMPLATE_TYPE_PURCHASE_AGREEMENT
} from "constants/templates";
import useEmail from "hooks/use-email.hook";
import { useGetStaffQuery } from "services/staff";
import type { StaffMember } from "services/types/staff.type";

const actionButtonStyles = {
  flex: 1,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: 0
};

type CustomPrintFormPreviewDialogProps = {
  title?: string;
};

export default function CustomPrintFormPreviewDialog({
  title = "Create my Custom Form…"
}: CustomPrintFormPreviewDialogProps) {
  const flags = useFlags();
  // ENG-2447: the "Fill in the blanks" panel beside the form, over SN-19949's fields. Part of HTML Forms
  // (ENG-2459); it renders nothing while the form holds no blank.
  const [editor, setEditor] = useState<Editor | null>(null);
  const blanksPanel = Boolean(flags[HTML_FORMS_FEATURE]);
  const purchaseAgreementTemplatesSectionEnabled =
    flags[PURCHASE_AGREEMENT_TEMPLATES_SECTION];
  const verticalSouthwesternLimitationsEnabled =
    flags[VERTICAL_SOUTHWESTERN_LIMITATIONS];
  const purchaseAgreementFooterSignerEnabled = Boolean(
    flags[PURCHASE_AGREEMENT_FOOTER_SIGNER]
  );
  const {
    patientId,
    formContent,
    setFormContent,
    hideDialog,
    dialogOpened,
    handleAskAi,
    isTemplateCompleting,
    autocompleteTextTemplate,
    handlePrint,
    enableHeader,
    setEnableHeader,
    isReady,
    enablePatientSignature,
    setEnablePatientSignature,
    enableProviderSignature,
    setEnableProviderSignature,
    currentTab,
    setCurrentTab,
    pdfFormBlobUrl,
    pdfGenerationError,
    handleRegeneratePdf,
    handleSaveToEdocs,
    isGeneratingPdfForEdocs,
    handleSubmitSignature,
    showSignModal,
    setShowSignModal,
    folderPickerOpen,
    setFolderPickerOpen,
    isEdocsAvailable,
    boxFolderId,
    boxAccessToken,
    handleSelectBoxFolder,
    fileName,
    handleShowSignModal,
    signature,
    patient,
    aiFeatureEnabled,
    providerSignatureEnabled,
    metadata,
    setWordProcessorFieldId,
    nativePdfTablesEnabled,
    setNativePdfTablesEnabled,
    numberOfPages,
    recalculateNumberOfPagesWithContent,
    footerSignerId,
    setFooterSignerId,
    footerSignatureDataUrl,
    setFooterSignatureDataUrl,
    showFooterSignModal,
    setShowFooterSignModal,
    signerLicenseOptions,
    printedLicenseNumber,
    pickSignerLicense,
    effectiveClinicId,
    digitalFormName,
    handleEmailPdf,
    isMakingEmailPdf
  } = useCustomPrintFormPreview();

  const { handleOpen, setInitialDocuments } = useEmail();

  const { data: clinicStaff = [] } = useGetStaffQuery(
    { clinicIds: [effectiveClinicId!], is_active: true },
    { skip: !effectiveClinicId || !purchaseAgreementFooterSignerEnabled }
  );

  const invoiceProviderId = metadata.staffMember?.id;
  const [shouldAutoOpenFooterSignModal, setShouldAutoOpenFooterSignModal] =
    useState(false);

  const currentFooterSigner = useMemo<StaffMember | null>(() => {
    const targetId = footerSignerId ?? invoiceProviderId;
    return clinicStaff.find((s) => s.id === targetId) ?? null;
  }, [footerSignerId, clinicStaff, invoiceProviderId]);

  // Dated when signed, a drawn signature prints at once, so someone has to be named under it first.
  const needsFooterSigner =
    Boolean(metadata.datesSignaturesWhenSigned) &&
    !footerSignerId &&
    !metadata.staffMember;

  const handleFooterSignerChange = useCallback(
    (_: React.SyntheticEvent, newValue: StaffMember | null) => {
      const newId = newValue?.id ?? null;
      setFooterSignerId(newId === invoiceProviderId ? null : newId);
      setFooterSignatureDataUrl(null);
      setShouldAutoOpenFooterSignModal(Boolean(newValue));
      handleRegeneratePdf();
    },
    [
      setFooterSignerId,
      setFooterSignatureDataUrl,
      handleRegeneratePdf,
      invoiceProviderId
    ]
  );

  const handleFooterSignSubmit = useCallback(
    (files: File[]) => {
      const file = files?.at(0);
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        setFooterSignatureDataUrl(reader.result as string);
        setShowFooterSignModal(false);
        handleRegeneratePdf();
      };
      reader.readAsDataURL(file);
    },
    [setFooterSignatureDataUrl, setShowFooterSignModal, handleRegeneratePdf]
  );

  useEffect(() => {
    if (!shouldAutoOpenFooterSignModal) return;
    if (footerSignatureDataUrl) {
      setShouldAutoOpenFooterSignModal(false);
      return;
    }

    if (!currentFooterSigner?.signature) {
      setShowFooterSignModal(true);
    }

    setShouldAutoOpenFooterSignModal(false);
  }, [
    currentFooterSigner,
    footerSignatureDataUrl,
    setShowFooterSignModal,
    shouldAutoOpenFooterSignModal
  ]);

  const defaultValues: { content: string } = useMemo(() => {
    return { content: formContent };
  }, [formContent]);

  const methods = useForm<{ content: string }>({
    defaultValues
  });

  const { control, getValues, reset, watch } = methods;
  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const handleSubmit = () => {
    const formData = getValues();
    setFormContent(formData.content);
    hideDialog();
  };

  const disableMergeTags = Boolean(!patientId);

  const onAskAi = useCallback(
    (params: {
      id: string;
      prompt: string;
      fieldContent: string;
      lastResult?: string;
    }) => {
      const { id, prompt, fieldContent, lastResult } = params;

      return handleAskAi?.({
        id,
        name: "Appointment printable form",
        type: PROMPT_TYPE_TEXT_TEMPLATE,
        prompt,
        fieldContent,
        lastResult
      });
    },
    [handleAskAi]
  );

  const handleEmailToPatient = useCallback(() => {
    const formData = getValues();
    // ENG-2277: a digital Form is sent as the PDF this dialog previews, prints and saves to E-Docs,
    // attached under the form's own name. As the email's text it would lose its page, the clinic
    // header and its signatures, and be laid out again by the patient's mail program. Any other
    // form is emailed as its text, as before.
    if (digitalFormName != null) {
      handleEmailPdf(formData.content, (pdfFile) => {
        setInitialDocuments([pdfFile]);
        handleOpen({ patient, emailSubject: digitalFormName });
      });
      return;
    }
    handleOpen({
      patient,
      emailContent: formData.content,
      emailSubject: "Appointment Form"
    });
  }, [
    digitalFormName,
    getValues,
    handleEmailPdf,
    handleOpen,
    patient,
    setInitialDocuments
  ]);

  const content = watch("content");

  // Debounced, single-flight page count recalculation while editing
  const pageCountDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const pageCalcInFlightRef = useRef<boolean>(false);
  const queuedContentRef = useRef<string | null>(null);

  const processPageCountQueue = useCallback(
    async (initialContent: string) => {
      pageCalcInFlightRef.current = true;
      try {
        let nextContent = initialContent;
        await recalculateNumberOfPagesWithContent(nextContent);
        // Drain any queued latest content, ensuring we only calculate for the newest
        while (
          queuedContentRef.current &&
          queuedContentRef.current !== nextContent
        ) {
          nextContent = queuedContentRef.current;
          queuedContentRef.current = null;
          await recalculateNumberOfPagesWithContent(nextContent);
        }
      } finally {
        pageCalcInFlightRef.current = false;
      }
    },
    [recalculateNumberOfPagesWithContent]
  );

  useEffect(() => {
    // Only calculate while user is on the editor tab
    if (currentTab !== "form") return;

    if (pageCountDebounceRef.current) {
      clearTimeout(pageCountDebounceRef.current);
    }

    pageCountDebounceRef.current = setTimeout(() => {
      const trimmed = (content || "").trim();
      if (!trimmed) return;

      if (pageCalcInFlightRef.current) {
        // Coalesce to the latest content
        queuedContentRef.current = content;
        return;
      }

      void processPageCountQueue(content);
    }, 1000);

    return () => {
      if (pageCountDebounceRef.current) {
        clearTimeout(pageCountDebounceRef.current);
      }
    };
  }, [content, currentTab, processPageCountQueue]);

  const templateTypes = useMemo(() => {
    return purchaseAgreementTemplatesSectionEnabled
      ? [...PRINTABLE_FORM_TEMPLATE_TYPES, TEMPLATE_TYPE_PURCHASE_AGREEMENT]
      : PRINTABLE_FORM_TEMPLATE_TYPES;
  }, [purchaseAgreementTemplatesSectionEnabled]);

  const handleOnContentChange = useCallback(
    (content: string, text: string, element: HTMLElement) => {
      // Just track that content changed, don't do expensive calculations
    },
    []
  );

  return (
    <Dialog
      title={title}
      minWidth={1324}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        hideDialog();
      }}
      open={dialogOpened}
      dialogContentProps={{
        sx: {
          display: "flex",
          alignItems: "flex-end",
          p: 3,
          pt: 0,
          flexDirection: "column"
        }
      }}
    >
      <Stack
        direction="row"
        mb={2}
        columnGap={3}
        mt={-1.5}
        width="100%"
        justifyContent="space-between"
      >
        <Stack width="100%" direction="row" columnGap={3}>
          {aiFeatureEnabled && (
            <Box>
              <IconButton
                data-testid="autocomplete-button"
                size="small"
                sx={actionButtonStyles}
                onClick={() => {
                  autocompleteTextTemplate(content);
                }}
                disabled={
                  isTemplateCompleting || !isReady || currentTab === "pdf"
                }
              >
                <AutoFixHighIcon />
                <Typography>Enhance Form</Typography>
              </IconButton>
            </Box>
          )}

          <Box>
            <IconButton
              data-testid="sign-button"
              size="small"
              sx={actionButtonStyles}
              disabled={isTemplateCompleting || !isReady}
              onClick={() => {
                setFormContent(content);
                handleShowSignModal();
              }}
            >
              <SignDocumentIcon
                style={{
                  height: "24px",
                  width: "24px",
                  fill:
                    isTemplateCompleting || !isReady
                      ? neutral[400]
                      : neutral[800]
                }}
              />
              <Typography>Sign</Typography>
            </IconButton>
          </Box>
          <Box>
            <IconButton
              data-testid="print-button"
              size="small"
              sx={actionButtonStyles}
              onClick={() => {
                handlePrint(content);
              }}
              disabled={isTemplateCompleting || !isReady}
            >
              <PrintIcon />
              <Typography>Print</Typography>
            </IconButton>
          </Box>
          <Box>
            <IconButton
              data-testid="upload-to-edocs-button"
              size="small"
              sx={actionButtonStyles}
              onClick={() => {
                setFormContent(content);
                handleSaveToEdocs();
              }}
              disabled={
                isTemplateCompleting || !isReady || isGeneratingPdfForEdocs
              }
            >
              {isGeneratingPdfForEdocs ? (
                <CircularProgress size={24} sx={{ p: 0 }} />
              ) : (
                <CloudUploadIcon />
              )}
              <Typography>Upload to&nbsp;E-Docs</Typography>
            </IconButton>
          </Box>
          <Box>
            <IconButton
              data-testid="email-to-patient-button"
              size="small"
              sx={actionButtonStyles}
              onClick={handleEmailToPatient}
              disabled={
                isTemplateCompleting || !isReady || !patient || isMakingEmailPdf
              }
            >
              {isMakingEmailPdf ? (
                <CircularProgress size={24} sx={{ p: 0 }} />
              ) : (
                <EmailIcon />
              )}
              <Typography>Email to&nbsp;Patient</Typography>
            </IconButton>
          </Box>
        </Stack>
        {!verticalSouthwesternLimitationsEnabled && (
          <Stack width="100%" direction="row" columnGap={3}>
            <FormControlLabel
              control={
                <Switch
                  disabled={isTemplateCompleting || !isReady}
                  checked={nativePdfTablesEnabled}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setNativePdfTablesEnabled(event.target.checked);
                    handleRegeneratePdf();
                  }}
                />
              }
              label="Enhanced Table Rendering"
            />
            <FormControlLabel
              control={
                <Switch
                  disabled={isTemplateCompleting || !isReady}
                  checked={enableHeader}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setEnableHeader(event.target.checked);
                    handleRegeneratePdf();
                  }}
                />
              }
              label={`Clinic${NON_BREAKABLE_SPACE}Header`}
            />
            {providerSignatureEnabled && (
              <Stack direction="column" gap={1} alignItems="flex-start">
                {purchaseAgreementFooterSignerEnabled && (
                  <>
                    <Stack direction="row" gap={1} alignItems="flex-end">
                      <Autocomplete<StaffMember>
                        size="small"
                        sx={{ minWidth: 220 }}
                        options={clinicStaff}
                        getOptionLabel={(option) =>
                          option.suffix
                            ? `${option.full_name}, ${option.suffix}`
                            : option.full_name
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        value={currentFooterSigner}
                        onChange={handleFooterSignerChange}
                        disabled={isTemplateCompleting || !isReady}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Footer Signer"
                            size="small"
                          />
                        )}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DrawIcon />}
                        disabled={
                          isTemplateCompleting || !isReady || needsFooterSigner
                        }
                        onClick={() => setShowFooterSignModal(true)}
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        {footerSignatureDataUrl ? "Re-sign" : "Sign now"}
                      </Button>
                    </Stack>
                    {needsFooterSigner && (
                      <Typography variant="caption" color="text.secondary">
                        Choose a Footer Signer first. Their name prints with the
                        signature.
                      </Typography>
                    )}
                  </>
                )}
                {enableProviderSignature && signerLicenseOptions.length > 1 && (
                  <TextField
                    select
                    size="small"
                    // Fixed, so a pick never reflows the toolbar; fits "District Of Columbia" and a
                    // 12-character license number.
                    sx={{ width: 360 }}
                    label="License on This PDF"
                    helperText="Printed under the provider signature. Changes this printout only, not the provider's profile."
                    FormHelperTextProps={{ sx: { typography: "body2" } }}
                    value={
                      signerLicenseOptions.some(
                        (option) => option.value === printedLicenseNumber
                      )
                        ? printedLicenseNumber
                        : ""
                    }
                    onChange={(event) => {
                      pickSignerLicense(event.target.value);
                      handleRegeneratePdf();
                    }}
                    // Not disabled while the PDF regenerates: disabling the focused select on
                    // every pick dropped keyboard focus to the dialog.
                    disabled={isTemplateCompleting}
                    inputProps={{ "data-testid": "signer-license-select" }}
                  >
                    {signerLicenseOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <FormControlLabel
                  control={
                    <Switch
                      disabled={isTemplateCompleting || !isReady}
                      checked={enableProviderSignature}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setEnableProviderSignature(event.target.checked);
                        handleRegeneratePdf();
                      }}
                    />
                  }
                  label={`Provider${NON_BREAKABLE_SPACE}Signature in${NON_BREAKABLE_SPACE}Footer`}
                />
              </Stack>
            )}

            <FormControlLabel
              control={
                <Switch
                  disabled={isTemplateCompleting || !isReady}
                  checked={enablePatientSignature}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setEnablePatientSignature(event.target.checked);
                    handleRegeneratePdf();
                  }}
                />
              }
              label={
                <Stack rowGap={1}>
                  <Typography>
                    Patient&nbsp;Signature&nbsp;in&nbsp;Footer
                  </Typography>
                  {!signature && (
                    <Typography sx={{ fontStyle: "italic" }}>
                      Not signed
                    </Typography>
                  )}
                </Stack>
              }
            />
          </Stack>
        )}
      </Stack>

      <Tabs
        centered
        variant="fullWidth"
        value={currentTab}
        onChange={(_, value) => {
          // Always sync latest editor content before switching
          setFormContent(content);

          if (currentTab !== value) {
            setCurrentTab(value);
          }

          // Ensure Preview shows the latest content: always regenerate
          if (value === "pdf") {
            handleRegeneratePdf();
          }
        }}
        sx={{
          width: "100%",
          mt: -2
        }}
      >
        <Tab label="Edit Form" value="form" />
        <Tab
          label={
            numberOfPages
              ? `Preview (${numberOfPages === 1 ? "1 page" : `${numberOfPages} pages`})`
              : "Preview"
          }
          disabled={isTemplateCompleting || !isReady}
          value="pdf"
        />
      </Tabs>

      {Boolean(metadata?.blankMergeTags?.length) && (
        <Alert
          severity="warning"
          sx={{ width: "100%", mb: 1.5 }}
          data-testid="blank-merge-tags"
        >
          {metadata.blankMergeTags?.length === 1
            ? "This field has no value and will print blank unless you type it in: "
            : "These fields have no value and will print blank unless you type them in: "}
          {metadata.blankMergeTags?.map(({ title }) => title).join(", ")}
        </Alert>
      )}
      {currentTab === "form" && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            width: "100%",
            alignItems: "flex-start"
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <WordProcessorField
              name="content"
              control={control}
              placeholder="Enter form content"
              height={640}
              autoFocus
              allowTemplates={!disableMergeTags}
              allowMergeTags={!disableMergeTags}
              allowInsertMergeTagsValues={!disableMergeTags}
              templateTypes={templateTypes}
              allowToolbarAutohide={false}
              mergeTagsPatientId={patientId}
              onChange={handleOnContentChange}
              onIdGenerated={setWordProcessorFieldId}
              onEditorInit={setEditor}
              askAiInputPlaceholder="Ask AI to complete or enhance your form..."
              askAiPromptType={PROMPT_TYPE_TEXT_TEMPLATE}
              onAskAi={onAskAi}
              allowAIPromptInterface={aiFeatureEnabled}
              isLoading={isTemplateCompleting || !isReady}
              patientSignature={signature}
              allowInvoiceMergeTags
              additionalMergeTagsValues={metadata?.additionalMergeTagsValues}
              formSettings={metadata?.formSettings}
            />
          </Box>
          {blanksPanel && <FillInTheBlanksPanel editor={editor} />}
        </Box>
      )}
      {currentTab === "pdf" && (
        <Box width="100%" height="640px">
          {/* The error branch comes first on purpose. A failed generation leaves
              pdfFormBlobUrl empty, which used to land on the spinner below and span
              forever — the clinician sees "Generating PDF preview..." for a document
              that will never arrive, with nothing to click. */}
          {pdfGenerationError ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <Alert
                severity="error"
                action={
                  <Button size="small" onClick={handleRegeneratePdf}>
                    Retry
                  </Button>
                }
              >
                The PDF preview could not be generated.
              </Alert>
            </Box>
          ) : pdfFormBlobUrl ? (
            <iframe
              src={pdfFormBlobUrl}
              width="100%"
              height="100%"
              style={{ border: "none", flexGrow: 1 }}
              title="PDF Preview"
            />
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              flexDirection="column"
              gap={2}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Generating PDF preview...
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Button
        size="small"
        variant="contained"
        sx={{ mt: 2 }}
        onClick={handleSubmit}
      >
        Close
      </Button>
      {Boolean(pdfFormBlobUrl) ? (
        <Box>
          <SignaturePicker
            open={showSignModal}
            onClose={() => setShowSignModal(false)}
            onSubmit={handleSubmitSignature}
            enableHandSignatureOnly
            enableTypedSignature
            showUploadToEdocsCheckbox
            signatureDeskTitle="Patient Signature"
            submitButtonText="Sign Form"
            entryPoint="custom-print-form"
            largeSize
          />
        </Box>
      ) : null}
      {purchaseAgreementFooterSignerEnabled && (
        // Deliberately NO `enableTypedSignature`: Type to Sign (SN-19542) is
        // scoped to PATIENT signing only. This footer captures the PROVIDER's
        // signature, which is an attestation of clinical work — it stays
        // draw-or-upload, same as the staff signature in Personal Details.
        <SignaturePicker
          open={showFooterSignModal}
          onClose={() => setShowFooterSignModal(false)}
          onSubmit={handleFooterSignSubmit}
          enableHandSignatureOnly
          signatureDeskTitle="Provider Signature"
          submitButtonText="Use Signature"
          entryPoint="report-footer"
          largeSize
        />
      )}
      <SaveToEdocsDialog
        open={folderPickerOpen}
        folderId={isEdocsAvailable ? boxFolderId : null}
        token={isEdocsAvailable ? boxAccessToken : null}
        suggestedFileName={fileName}
        onChoose={handleSelectBoxFolder}
        onClose={() => {
          setFolderPickerOpen(false);
        }}
      />
    </Dialog>
  );
}
